import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Card, CardContent,
  Checkbox, FormControlLabel, CircularProgress, Stack, Link as MuiLink,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import ExploreIcon from '@mui/icons-material/Explore';
import GroupsIcon from '@mui/icons-material/Groups';
import MapIcon from '@mui/icons-material/Map';
import { API_URL } from '../utils/api';
import { trackEvent } from '../utils/analytics';
import { COLORS } from '../utils/colors';

/**
 * WelcomePage (/welcome)
 * The sendable "controlled introduction" page. Demo-central, one access CTA.
 * Distinct from /early-access (the lean form for users already on the site)
 * and / (the full marketing landing page). Reuses the early-access request
 * flow (POST /early-access + trackEvent('early_access_requested')).
 */
const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountExists, setAccountExists] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim() || !acceptedTerms) return;
    setLoading(true);
    setError('');
    setAccountExists(false);
    try {
      const res = await fetch(`${API_URL}/early-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject: 'Early Access Request — ' + name.trim(),
          message: 'Requesting early access to Percoro (via /welcome).',
        }),
      });
      const data = await res.json();
      if (data.success) {
        trackEvent('early_access_requested');
        setSubmitted(true);
      } else if (data.error === 'account_exists') {
        setAccountExists(true);
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const navLinkSx = {
    color: COLORS.primary, fontWeight: 600, fontSize: '0.95rem',
    textDecoration: 'none', cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFB' }}>
      {/* Top bar */}
      <Box sx={{ px: { xs: 3, md: 6 }, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          onClick={() => navigate('/')}
          sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '1.5rem', color: COLORS.primary, cursor: 'pointer' }}
        >
          PERCORO
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/demo')}
          startIcon={<PlayCircleFilledIcon />}
          sx={{ borderColor: COLORS.primary, color: COLORS.primary, borderRadius: '24px', textTransform: 'none', fontWeight: 600 }}
        >
          Try the demo
        </Button>
      </Box>

      {/* Hero hook */}
      <Box sx={{ px: { xs: 3, md: 6 }, pt: { xs: 2, md: 4 }, pb: 2, maxWidth: 1100, mx: 'auto', textAlign: 'center' }}>
        <Box sx={{ display: 'inline-block', bgcolor: '#EAF5F3', color: COLORS.primary, px: 2, py: 0.5, borderRadius: '20px', fontWeight: 700, fontSize: '0.85rem', mb: 2 }}>
          ● Percoro is live — private early access
        </Box>
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' }, color: '#1F2A2E', lineHeight: 1.15, mb: 2 }}>
          Plan, document, and share your real trips.
        </Typography>
        <Typography sx={{ fontSize: { xs: '1.05rem', md: '1.2rem' }, color: '#55636B', maxWidth: 680, mx: 'auto', lineHeight: 1.6 }}>
          Percoro is a social travel platform for the journeys you actually take — itineraries, photos,
          and the people you travel with, all in one place. Take a look, then request access.
        </Typography>
      </Box>

      {/* DEMO — central */}
      <Box sx={{ px: { xs: 2, md: 6 }, pt: 2, pb: 4, maxWidth: 980, mx: 'auto' }}>
        <Card
          onClick={() => navigate('/demo')}
          sx={{
            position: 'relative', borderRadius: 4, overflow: 'hidden', cursor: 'pointer',
            background: `linear-gradient(135deg, ${COLORS.primary} 0%, #1F4E4A 100%)`,
            boxShadow: '0 20px 60px rgba(0,180,160,0.25)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 28px 70px rgba(0,180,160,0.32)' },
          }}
        >
          <Box sx={{ px: { xs: 4, md: 8 }, py: { xs: 6, md: 9 }, textAlign: 'center', color: 'white' }}>
            <PlayCircleFilledIcon sx={{ fontSize: { xs: 64, md: 88 }, mb: 2, opacity: 0.95 }} />
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.9rem' }, mb: 1 }}>
              See Percoro in action
            </Typography>
            <Typography sx={{ fontSize: { xs: '0.95rem', md: '1.05rem' }, opacity: 0.9, maxWidth: 520, mx: 'auto', lineHeight: 1.6 }}>
              A guided walkthrough of a real trip inside Percoro — planning, photos, and sharing.
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 3, bgcolor: 'white', color: COLORS.primary, borderRadius: '28px', px: 4, py: 1.2, textTransform: 'none', fontWeight: 700, fontSize: '1rem', '&:hover': { bgcolor: '#F0FFFD' } }}
            >
              Watch the demo
            </Button>
          </Box>
        </Card>

        {/* three quick value points */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
          {[
            { icon: <MapIcon sx={{ color: COLORS.primary }} />, t: 'Plan real trips', d: 'Build itineraries and import your existing plans.' },
            { icon: <ExploreIcon sx={{ color: COLORS.primary }} />, t: 'Document the journey', d: 'Photos, captions, and the route you actually took.' },
            { icon: <GroupsIcon sx={{ color: COLORS.primary }} />, t: 'Share with your circle', d: 'Travel companions, friends, and a community feed.' },
          ].map((v) => (
            <Card key={v.t} variant="outlined" sx={{ flex: 1, borderRadius: 3, p: 0.5 }}>
              <CardContent>
                {v.icon}
                <Typography sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>{v.t}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>{v.d}</Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>

      {/* Request access */}
      <Box sx={{ px: { xs: 3, md: 6 }, pb: 5, maxWidth: 520, mx: 'auto' }}>
        <Card sx={{ borderRadius: 4, boxShadow: '0 8px 40px rgba(0,0,0,0.08)' }}>
          <Box sx={{ bgcolor: COLORS.primary, borderRadius: '16px 16px 0 0', px: 4, py: 3, textAlign: 'center' }}>
            <Typography sx={{ color: 'white', fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '1.25rem' }}>
              Request early access
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem', mt: 0.5 }}>
              A small group is getting in first. Expect a few rough edges — your feedback shapes it.
            </Typography>
          </Box>
          <CardContent sx={{ p: 4 }}>
            {submitted ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircleIcon sx={{ color: COLORS.success, fontSize: 56, mb: 2 }} />
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Request received!</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                  Thanks for your interest in Percoro. We&apos;ll be in touch shortly with your invitation.
                </Typography>
                <Button variant="outlined" onClick={() => navigate('/')}
                  sx={{ borderColor: COLORS.primary, color: COLORS.primary, borderRadius: '24px', textTransform: 'none', fontWeight: 600 }}>
                  Back to Percoro
                </Button>
              </Box>
            ) : accountExists ? (
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#EAF5F3', border: '1px solid ' + COLORS.primary }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: COLORS.primary, mb: 0.5 }}>
                  You already have a Percoro account
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.5 }}>
                  No need to request access — log in, or reset your password if you&apos;ve forgotten it.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="contained" onClick={() => navigate('/login')}
                    sx={{ bgcolor: COLORS.primary, textTransform: 'none', borderRadius: '20px', fontWeight: 600, '&:hover': { bgcolor: COLORS.primaryDark } }}>
                    Log in
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => navigate('/forgot-password')}
                    sx={{ borderColor: COLORS.primary, color: COLORS.primary, textTransform: 'none', borderRadius: '20px', fontWeight: 600 }}>
                    Reset password
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                <TextField fullWidth label="Your name" value={name} onChange={(e) => setName(e.target.value)}
                  sx={{ mb: 2 }} size="small" />
                <TextField fullWidth label="Email address" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  sx={{ mb: 2 }} size="small" />
                {error && <Typography variant="body2" sx={{ color: COLORS.error, mb: 2 }}>{error}</Typography>}
                <FormControlLabel
                  control={<Checkbox checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary } }} />}
                  label={
                    <Typography variant="body2" color="text.secondary">
                      I am at least 18 and agree to the{' '}
                      <MuiLink onClick={() => navigate('/terms')} sx={{ color: COLORS.primary, cursor: 'pointer' }}>Terms</MuiLink> and{' '}
                      <MuiLink onClick={() => navigate('/privacy')} sx={{ color: COLORS.primary, cursor: 'pointer' }}>Privacy Policy</MuiLink>.
                    </Typography>
                  }
                  sx={{ mb: 2, alignItems: 'flex-start' }}
                />
                <Button fullWidth variant="contained" onClick={handleSubmit}
                  disabled={loading || !name.trim() || !email.trim() || !acceptedTerms}
                  sx={{ bgcolor: COLORS.primary, color: 'white', borderRadius: '28px', py: 1.3, textTransform: 'none', fontWeight: 700, fontSize: '1rem', '&:hover': { bgcolor: '#1F4E4A' } }}>
                  {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Request early access'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Explore more */}
      <Box sx={{ px: 3, pb: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Explore more</Typography>
        <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap" useFlexGap>
          <Box component="span" onClick={() => navigate('/')} sx={navLinkSx}>Home</Box>
          <Box component="span" onClick={() => navigate('/about')} sx={navLinkSx}>About</Box>
          <Box component="span" onClick={() => navigate('/demo')} sx={navLinkSx}>Demo</Box>
          <Box component="span" onClick={() => navigate('/resources')} sx={navLinkSx}>Blog</Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default WelcomePage;
